
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import cicliiHero from '../../assets/ciclii3.png';
import Loader from '../../components/Loader';
import { routes } from '../../routes/routeConfig';
import UsersService from '../../services/UsersService';
import { decodeFromBase64Url } from '../../utils/base64Url';
import { isValidEmailWithCommonTLD } from '../../utils/emailValidation';

interface ParsedActivationToken {
    email: string;
    userId: number | null;
}

const parseActivationToken = (token: string): ParsedActivationToken | null => {
    if (!token) {
        return null;
    }

    try {
        const jsonPayload = decodeFromBase64Url(token);
        const parsed = JSON.parse(jsonPayload) as { email?: unknown; id_usuario?: unknown };

        const emailValue = typeof parsed.email === 'string' ? parsed.email.trim() : '';

        const rawUserId = parsed.id_usuario;

        let numericUserId: number | null = null;
        if (typeof rawUserId === 'number' && Number.isFinite(rawUserId)) {
            numericUserId = rawUserId;
        } else if (typeof rawUserId === 'string') {
            const parsedNumber = Number.parseInt(rawUserId, 10);
            numericUserId = Number.isNaN(parsedNumber) ? null : parsedNumber;
        }

        if (!emailValue) {
            return null;
        }

        return {
            email: emailValue,
            userId: numericUserId,
        };
    } catch (error) {
        console.error('No fue posible interpretar el token de activación.', error);
        return null;
    }
};

type ActivationStatus = 'loading' | 'invalid' | 'error' | 'ready' | 'already-active' | 'activated' | 'activation-failed';

const ActivateAccount: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const tokenParam = (searchParams.get('activation') ?? '').trim();

    const tokenData = useMemo(() => parseActivationToken(tokenParam), [tokenParam]);
    const emailParam = tokenData?.email ?? '';
    const parsedUserId = tokenData?.userId ?? null;

    const [status, setStatus] = useState<ActivationStatus>('loading');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [activationError, setActivationError] = useState<string>('');
    const [activationFeedback, setActivationFeedback] = useState<string>('');
    const [activating, setActivating] = useState<boolean>(false);
    const [activationStarted, setActivationStarted] = useState<boolean>(false);
    const supportEmail = (process.env.REACT_APP_SUPORT_EMAIL || 'soporte@ciclii.com').trim();

    useEffect(() => {
        const trimmedEmail = emailParam;
        setActivationStarted(false);
        setStatus('loading');
        setErrorMessage('');
        setActivationError('');
        setActivationFeedback('');

        const isMissingEmail = !trimmedEmail;
        const isInvalidEmail = !isMissingEmail && !isValidEmailWithCommonTLD(trimmedEmail);
        const isInvalidUserId = parsedUserId === null || parsedUserId <= 0;

        if (isMissingEmail || isInvalidEmail || isInvalidUserId) {
            setStatus('invalid');
            setErrorMessage('El enlace de activación no es válido.');
            return;
        }

        setStatus('ready');
    }, [emailParam, parsedUserId]);

    const handleActivate = useCallback(async () => {
        if (parsedUserId === null || parsedUserId <= 0 || !emailParam) {
            return;
        }

        setActivationError('');
        setActivationFeedback('');
        setActivating(true);

        try {
            const response = await UsersService.updateUser({ id_usuario: parsedUserId, email: emailParam, estado: true });
            if (response.statusCode === 200) {
                setActivationFeedback('Tu cuenta fue activada correctamente. Ya puedes iniciar sesión.');
                setStatus('activated');
            } else {
                setActivationError(response.message || 'No pudimos activar tu cuenta automáticamente.');
                setStatus('activation-failed');
            }
        } catch (error: any) {
            const fallbackMessage = error?.response?.data?.message || 'No pudimos activar tu cuenta automáticamente.';
            setActivationError(fallbackMessage);
            setStatus('activation-failed');
        } finally {
            setActivating(false);
        }
    }, [parsedUserId, emailParam]);

    useEffect(() => {
        if (status === 'ready' && !activating && !activationStarted) {
            setActivationStarted(true);
            void handleActivate();
        }
    }, [status, activating, activationStarted, handleActivate]);

    const handleGoToLogin = () => navigate(routes.public.login);

    return (
        <div className='landing-container'>
            <div className='login-card activation-card'>
                <img src={cicliiHero} alt='Ilustración Ciclii' style={{ width: 140, margin: '0 auto 24px', display: 'block' }} />
                <h1 style={{ color: '#b57edc' }}>Activa tu cuenta</h1>

                {(status === 'loading' || activating) && (
                    <Loader text={activating ? 'Activando tu cuenta...' : 'Validando tu enlace...'} />
                )}

                {status === 'invalid' && (
                    <>
                        <p className='activation-feedback'>{errorMessage}</p>
                        <p className='activation-feedback'>
                            Por favor, contáctanos en{' '}
                            <a
                                href={`mailto:${supportEmail}`}
                                style={{ color: '#764ba2', textDecoration: 'none', fontWeight: 600 }}
                            >
                                {supportEmail}
                            </a>
                            .
                        </p>
                        <div className='activation-actions'>
                            <button type='button' className='btn btn-primary' onClick={handleGoToLogin}>
                                Ir a Ciclii
                            </button>
                        </div>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <p className='activation-feedback'>{errorMessage}</p>
                        <p className='activation-feedback'>
                            Por favor, contáctanos en{' '}
                            <a
                                href={`mailto:${supportEmail}`}
                                style={{ color: '#764ba2', textDecoration: 'none', fontWeight: 600 }}
                            >
                                {supportEmail}
                            </a>
                            .
                        </p>
                        <div className='activation-actions'>
                            <button type='button' className='btn btn-primary' onClick={handleGoToLogin}>
                                Ir a Ciclii
                            </button>
                        </div>
                    </>
                )}

                {status === 'ready' && (
                    <p>Estamos activando tu cuenta. Esto puede tomar unos segundos.</p>
                )}

                {status === 'already-active' && (
                    <>
                        <p className='activation-feedback'>
                            El enlace ha caducado. Si tienes problemas, contáctanos en{' '}
                            {supportEmail ? (
                                <a
                                    href={`mailto:${supportEmail}`}
                                    style={{ color: '#764ba2', textDecoration: 'none', fontWeight: 600 }}
                                >
                                    <b>
                                        {supportEmail}
                                    </b>
                                </a>
                            ) : (
                                'nuestro correo de soporte'
                            )}
                            .
                        </p>
                        <div className='activation-actions'>
                            <button type='button' className='btn btn-primary' onClick={handleGoToLogin}>
                                Ir a Ciclii
                            </button>
                        </div>
                    </>
                )}

                {status === 'activated' && (
                    <>
                        <div className='activation-feedback'>{activationFeedback || 'Tu cuenta fue activada correctamente. Ya puedes iniciar sesión.'}</div>
                        <div className='activation-actions'>
                            <button type='button' className='btn btn-primary' onClick={handleGoToLogin}>
                                Iniciar sesión
                            </button>
                        </div>
                    </>
                )}

                {status === 'activation-failed' && (
                    <>
                        <p>No pudimos activar tu cuenta automáticamente.</p>
                        {activationError && <div className='activation-error'>{activationError}</div>}
                        <p className='activation-feedback'>
                            Por favor, contáctanos en{' '}
                            <a
                                href={`mailto:${supportEmail}`}
                                style={{ color: '#764ba2', textDecoration: 'none', fontWeight: 600 }}
                            >
                                {supportEmail}
                            </a>
                            .
                        </p>
                        <div className='activation-actions'>
                            <button type='button' className='btn btn-primary' onClick={handleGoToLogin}>
                                Ir a Ciclii
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ActivateAccount;